/**
 * @file: useAssociateReservationByPlate.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * When the staff enter/scan a license plate while creating an order NOT from a
 * reservation, search the plate's open reservations (pending / confirmed /
 * arrived). If any exist, surface them in a bottom panel to pick one — the
 * chosen `reservationId` rides into the create request so the backend converts
 * that reservation (and its parent lead) once the order is placed. Each plate
 * change re-searches from scratch, so a stale link can't outlive its plate.
 *
 * State (the candidate list) lives here; the host renders the sheet bound to
 * `candidates` / `select` / `dismiss`.
 */

import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { Reservation } from '@euler/model/reservation/Reservation';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useRef, useState } from 'react';
import Toast from 'react-native-root-toast';

export const useAssociateReservationByPlate = () => {
  const {
    formController: { form },
  } = useOrderFlowContext();
  const { reservationService } = useServiceFactory();
  // Guards against a slower earlier search landing after a newer plate's.
  const seqRef = useRef(0);
  const [candidates, setCandidates] = useState<Reservation[]>([]);

  const search = usePersistFn(async (plate: string) => {
    const p = plate.trim();
    setCandidates([]);
    if (!p) {
      form.reservationId.update(undefined);
      return;
    }

    const seq = ++seqRef.current;
    let reservations: Reservation[] = [];
    try {
      reservations = await reservationService.list(
        { licensePlateNo: p, status: ['pending', 'confirmed', 'arrived'] },
        0,
        20,
      );
    } catch (e) {
      console.error(e);
      return;
    }
    if (seq !== seqRef.current) return;

    // The plate filter is LIKE, so narrow to an exact (case-insensitive) match —
    // otherwise `川A1234` would also surface `川A12345`'s reservation.
    const matches = reservations.filter(
      r => (r.licensePlateNo ?? '').toUpperCase() === p.toUpperCase(),
    );

    // Already linked to one of this plate's reservations (e.g. the from-
    // reservation entry seeded it) — leave it untouched, don't re-prompt.
    const current = form.reservationId.value;
    if (current && matches.some(r => r.id === current)) return;

    // Otherwise any prior link was for a different plate — drop it, then offer
    // to associate one of this plate's reservations (empty → sheet stays hidden).
    form.reservationId.update(undefined);
    setCandidates(matches);
  });

  const select = usePersistFn((id: string) => {
    form.reservationId.update(id);
    setCandidates([]);
    Toast.show('已关联预约', { position: Toast.positions.BOTTOM });
  });

  const dismiss = usePersistFn(() => setCandidates([]));

  return { search, candidates, select, dismiss };
};
